import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useState, useCallback } from "react";

// react-router (v7) exports the DOM APIs directly; the react-router-dom
// re-export package is ESM-only and unresolvable in the Jest runner.
import { useParams, Link, useNavigate } from "react-router";
import {
  EditSite,
  getSite,
  clearMessage,
} from "@/services/siteServices/siteActions";
import { getAll } from "@/services/designServices/designAction";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { setErrors } from "@/services/errorServices/errorAction";
import { setAppMessage } from "@/services/appMessageServices/appMessageActions";
import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
} from "@/utils/selectOptions";
import UpdateSiteView from "./UpdateSiteView";
import { asCaught } from "@/services/caughtError";
import type { SiteRecord } from "./UpdateSiteView";

/** What ImageUploadContainer hands back: a picked file, or a media-library pick. */
type PickedImage = {
  data?: string;
  imgName?: string;
  secure_url?: string;
  context?: unknown;
};

export default function UpdateSiteContainer() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const navigate = useNavigate();
  const sites     = useAppSelector((s) => s.sites);
  const designs   = useAppSelector((s) => s.designs);
  const permission = useCurrentPermission();

  const [loading, setLoading] = useState(false);
  // Read only through `?.data` / `?.secure_url`, so the empty start is null.
  const [logoPic, setLogoPic] = useState<PickedImage | null>(null);
  // The blank form until GET /api/sites/:id answers; then the stored site.
  // SiteRecord is the server typedef LeanSite, whose `_id` is optional: the blank
  // form has no id until the stored site arrives (SCRUM-2111).
  const [site, setSite] = useState<SiteRecord>({
    name: "",
    domain: "",
    canonial_url: "",
    country_code: "",
    language_code: "",
    design: { _id: "", name: "", source: "" },
    logo_pic: "",
    analytics_code: "",
    gtm: "",
    special_style_files: "",
    special_script_files: "",
  });

 
  const designOptions = useMemo(() => {
    const list = designs?.designs || [];
    return list.map((d) => ({ value: { _id: d._id }, label: d.name }));
  }, [designs]);


  useEffect(() => {
    dispatch(getAll());
    dispatch(getSite(id as string));
  }, [dispatch, id]);

  
  useEffect(() => {
    if (sites?.siteToUpdate) setSite(sites.siteToUpdate);
  }, [sites?.siteToUpdate]);

  useEffect(() => {
    if (sites?.message) {
      // Route the success toast through the shell-level notification hub
      // (GlobalNotifications, SCRUM-883) rather than the default react-toastify
      // container — this view never mounts one, so a direct
      // NotificationAlertOptions("success", ...) call rendered nothing. The
      // redirect below stays driven by this view's OWN signal (sites.message),
      // per the SCRUM-883 note against reading/clearing the shared appMessage
      // slice for side-effects.
      dispatch(setAppMessage("success", sites.message));
      setLoading(false);
      dispatch(clearMessage());
      setTimeout(() => navigate("/admin/sites"), 800);
    }
  }, [sites?.message, dispatch]);

  // handlers
  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSite((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeCountry = useCallback((opt: { value: string } | null) => {
    setSite((prev) => ({ ...prev, country_code: opt ? opt.value : "" }));
  }, []);

  const onChangeLanguage = useCallback((opt: { value: string } | null) => {
    setSite((prev) => ({ ...prev, language_code: opt ? opt.value : "" }));
  }, []);

  const onChangeDesign = useCallback((opt: { value?: { _id?: string } } | null) => {
    setSite((prev) => ({
      ...prev,
      design: { ...prev.design, _id: opt?.value?._id || "" },
    }));
  }, []);

  const onToggleActive = useCallback(() => {
    setSite((prev) => ({ ...prev, active: !prev.active }));
  }, []);

  const onImageUpload = useCallback((img: PickedImage | null) => {
    setLogoPic(img);
  }, []);

  // A rejected EditSite has already pushed its error to state.errors for the hub
  // (GET_ERRORS); this view only clears its own spinner.
  const saveSite = useCallback(
    async (payload: SiteRecord) => {
      try {
        await dispatch(EditSite(payload)).unwrap();
      } catch {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const onClickUpdate = useCallback(async () => {
  
    if (logoPic?.data) {
      const cc = site.country_code || "";
      const path = `/${cc}/SITES/${logoPic.imgName}`;
      setLoading(true);
      // Linearised (SCRUM-1021): upload via the shared useImageUpload hook, then
      // save inline with the returned URL — no more state.images.url watch-effect
      // (the old success-only effect dropped the save with no toast when the
      // upload failed). A failed upload re-throws a plain Error (SCRUM-815) that
      // routes nowhere, so this view (no local ToastContainer) surfaces it
      // through the shell hub's error channel (state.errors) and clears the
      // spinner.
      try {
        const url = await uploadImage(logoPic.data, path);
        await saveSite({ ...site, logo_pic: url });
      } catch (err) {
        setLoading(false);
        dispatch(
          setErrors({
            message:
              asCaught(err)?.message || "Uploading the logo failed. Please try again.",
          })
        );
      }
      return;
    }


    if (logoPic?.secure_url) {
      await saveSite({ ...site, logo_pic: logoPic.secure_url });
      return;
    }

   
    await saveSite(site);
  }, [saveSite, uploadImage, logoPic, site, dispatch]);

  const canEdit = !!(permission?.edit || permission?.publish);

  return (
    <UpdateSiteView
      site={site}
      countryOptions={COUNTRY_OPTIONS}
      languageOptions={LANGUAGE_OPTIONS}
      designOptions={designOptions}
      loading={loading}
      canEdit={canEdit}
      onChange={onChange}
      onChangeCountry={onChangeCountry}
      onChangeLanguage={onChangeLanguage}
      onChangeDesign={onChangeDesign}
      onToggleActive={onToggleActive}
      onImageUpload={onImageUpload}
      onClickUpdate={onClickUpdate}
      backLink={"/admin/sites"}
    />
  );
}
